import { Button } from '@/components/ui';
import type { ProjectInstance, TabId } from './types';

interface OverviewTabProps {
  project: ProjectInstance;
  onOpenDocument: (doc: {
    id: string;
    documentNumber?: string;
    projectId?: string;
    title: string;
    type: 'project' | 'company' | 'standard' | 'prior-project';
    canEdit?: boolean;
    status?: string;
    currentRevision?: string;
  }) => void;
  setActiveTab: (tab: TabId) => void;
}

export function OverviewTab({ project, onOpenDocument, setActiveTab }: OverviewTabProps) {
  const docCount = project.documents?.length || 0;
  const draftCount = project.documents?.filter(d => d.status === 'Draft').length || 0;
  const releasedCount = project.documents?.filter(d => d.status === 'Released').length || 0;
  const inReviewCount = project.documents?.filter(d => d.status === 'In Review').length || 0;

  const handleDocumentClick = (doc: { documentNumber: string; status: string; projectSpecificTitle?: string; documentTitle?: string; currentRevision?: string }) => {
    const canEdit = doc.status === 'Draft' || doc.status === 'In Review';
    onOpenDocument({
      id: doc.documentNumber,
      documentNumber: doc.documentNumber,
      projectId: project.id,
      title: doc.projectSpecificTitle || doc.documentTitle,
      type: 'project',
      canEdit,
      status: doc.status,
      currentRevision: doc.currentRevision,
    });
  };

  return (
    <div className="space-y-4">
      {/* Quick Stats */}
      <div className="bg-muted/30 rounded-lg p-4 grid grid-cols-4 gap-3">
        <StatCard label="Total" value={docCount} color="blue" />
        <StatCard label="Released" value={releasedCount} color="green" />
        <StatCard label="In Review" value={inReviewCount} color="purple" />
        <StatCard label="Draft" value={draftCount} color="amber" />
      </div>
      
      {/* Project Information */}
      <div className="bg-muted/30 rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground">
          Project Information
        </h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {project.customer && (
            <InfoRow label="Customer" value={project.customer} />
          )}
          {project.program && (
            <InfoRow label="Program" value={project.program} />
          )}
          {project.certificationAuthority && (
            <InfoRow label="Cert Authority" value={project.certificationAuthority} />
          )}
          <InfoRow 
            label="Created" 
            value={new Date(project.createdAt).toLocaleDateString()} 
          />
          {project.updatedAt && (
            <InfoRow 
              label="Last Updated" 
              value={new Date(project.updatedAt).toLocaleDateString()} 
            />
          )}
        </div>
        
        {/* Certification Basis */}
        {project.certificationBasis && project.certificationBasis.length > 0 && (
          <div className="mt-4">
            <span className="text-xs font-medium text-muted-foreground block mb-2">
              Certification Basis
            </span>
            <div className="flex flex-wrap gap-1.5">
              {project.certificationBasis.map(basis => (
                <span
                  key={basis}
                  className="text-xs px-2 py-0.5 bg-info-light text-info rounded"
                >
                  {basis}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Recent Documents */}
      {project.documents && project.documents.length > 0 && (
        <div className="bg-muted/30 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">
            Recent Documents
          </h3>
          <div className="space-y-1">
            {project.documents.slice(0, 5).map(doc => (
              <Button
                key={doc.documentNumber}
                onClick={() => handleDocumentClick(doc)}
                variant="ghost"
                className="w-full justify-start h-auto p-2"
              >
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="flex-1 min-w-0 text-left">
                    <span className="text-sm font-medium text-info">
                      {doc.documentNumber}
                    </span>
                    <p className="text-xs text-muted-foreground truncate">
                      {doc.projectSpecificTitle || doc.documentTitle}
                    </p>
                  </div>
                  <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded ${
                    doc.status === 'Released' ? 'bg-success-light text-success' :
                    doc.status === 'Approved' ? 'bg-info-light text-info' :
                    doc.status === 'In Review' ? 'bg-secondary text-secondary-foreground' :
                    'bg-warning-light text-warning'
                  }`}>
                    {doc.status}
                  </span>
                </div>
              </Button>
            ))}
          </div>
          {project.documents.length > 5 && (
            <Button
              onClick={() => setActiveTab('documents')}
              variant="link"
              size="sm"
              className="mt-2 text-info"
            >
              View all {project.documents.length} documents →
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colorClasses = {
    blue: 'text-info',
    green: 'text-success',
    purple: 'text-muted-foreground',
    amber: 'text-warning',
  };
  
  return (
    <div className="text-center py-2">
      <div className={`text-2xl font-bold ${colorClasses[color as keyof typeof colorClasses]}`}>
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}
